import { type Dispatch, type SetStateAction, useEffect } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { areGuideSettingsDialogShortcutsDisabled } from '@/lib/keyboard/dialog-shortcuts'
import {
  type CanvasKeyActionContext,
  runCanvasKeyAction,
} from '../handlers/canvas-key-action-handlers'
import { resolveCanvasKeyAction } from '../helpers/canvas-key-action'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * The canvas keyboard shortcuts (window keydown): selection, clipboard, lock/freeze, grouping,
 * z-order, nudging, guide editing, tool keys, marquee zoom's Z, and Escape's mode exits. The
 * table itself is `resolveCanvasKeyAction`; each action runs from
 * `handlers/canvas-key-action-handlers.ts`.
 */
export function useCanvasKeyboardShortcuts({
  applyPanScroll,
  bleedHeight,
  bleedWidth,
  isInShapeCreationMode,
  isInPenToolMode,
  isNodeEditMode,
  isInTextCreationMode,
  activeItem,
  isPanInteractionActive,
  isPanModeActive,
  isTextToolActive,
  isEditableTarget,
  cancelTextLinking,
  isTextLinkingActive,
  canSelectShapes,
  onDeactivatePanMode,
  onSelectAllApplied,
  onSelectNoneApplied,
  onBackgroundClick,
  clearShapeSelectionWithHistory,
  clearUnifiedSelectionWithHistory,
  copySelectionToClipboard,
  cutSelectionToClipboard,
  cutSelectionForActiveMode,
  isTextEditSessionActive,
  selectAllForActiveTextEdit,
  handleDeleteKeyWhileTextEditing,
  refocusActiveTextEditor,
  deleteSelectedObjects,
  lockSelectedObjects,
  unlockSelectedObjects,
  toggleFreezeSelectedObjects,
  pasteFromClipboard,
  onDeleteShapesHandled,
  updateGuides,
  nudgeDistancePt,
  onShapesNudge,
  onLayerReorder,
  onUnifiedLayerArrange,
  onActivateTextTool,
  onActivateSelectTool,
  activeIsolationGroupId,
  exitGroupIsolationMode,
  onFreezeSelected,
  nodeEditShapeId,
  onNodeEditModeChange,
  canUnifiedBringForward,
  canUnifiedSendBackward,
  setGroupIsolationPath,
  setNodeEditShapeData,
  setNodeEditShapeId,
  exitNodeEditMode,
  internalShapeManagerRef,
  scrollRef,
  selectedGuideIdsRef,
  setSelectedGuideIds,
  zMarqueeKeyHeldRef,
  setZMarqueeKeyHeld,
  setIsMarqueeZoomMode,
  setMarqueeZoom,
}: CanvasKeyActionContext & {
  isInPenToolMode: boolean
  isInTextCreationMode: boolean
  isPanInteractionActive: boolean
  isPanModeActive: boolean
  isEditableTarget: (target: EventTarget | null) => boolean
  canSelectShapes: boolean
  onFreezeSelected: CenterCanvasConfig['onFreezeSelected']
  onNodeEditModeChange: CenterCanvasConfig['onNodeEditModeChange']
  setNodeEditShapeData: Dispatch<SetStateAction<ShapeData | null>>
  setNodeEditShapeId: Dispatch<SetStateAction<string | null>>
}) {
  // ── Keyboard shortcuts ──
  useEffect(() => {
    const context: CanvasKeyActionContext = {
      internalShapeManagerRef,
      scrollRef,
      applyPanScroll,
      bleedWidth,
      bleedHeight,
      activeItem,
      isInShapeCreationMode,
      isNodeEditMode,
      nodeEditShapeId,
      exitNodeEditMode,
      isTextToolActive,
      isTextLinkingActive,
      cancelTextLinking,
      activeIsolationGroupId,
      exitGroupIsolationMode,
      setGroupIsolationPath,
      zMarqueeKeyHeldRef,
      setZMarqueeKeyHeld,
      setIsMarqueeZoomMode,
      setMarqueeZoom,
      selectedGuideIdsRef,
      setSelectedGuideIds,
      updateGuides,
      clearShapeSelectionWithHistory,
      clearUnifiedSelectionWithHistory,
      copySelectionToClipboard,
      cutSelectionToClipboard,
      cutSelectionForActiveMode,
      pasteFromClipboard,
      isTextEditSessionActive,
      selectAllForActiveTextEdit,
      handleDeleteKeyWhileTextEditing,
      refocusActiveTextEditor,
      deleteSelectedObjects,
      lockSelectedObjects,
      unlockSelectedObjects,
      toggleFreezeSelectedObjects,
      canUnifiedBringForward,
      canUnifiedSendBackward,
      nudgeDistancePt,
      onShapesNudge,
      onLayerReorder,
      onUnifiedLayerArrange,
      onDeactivatePanMode,
      onSelectAllApplied,
      onSelectNoneApplied,
      onBackgroundClick,
      onDeleteShapesHandled,
      onActivateTextTool,
      onActivateSelectTool,
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (areGuideSettingsDialogShortcutsDisabled()) return

      const isEditable = isEditableTarget(e.target)
      const action = resolveCanvasKeyAction({
        key: e.key,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        altKey: e.altKey,
        shiftKey: e.shiftKey,
        isEditable,
        isPanModeActive,
        isPanInteractionActive,
        hasActiveIsolationGroup: Boolean(activeIsolationGroupId),
        hasSelectedGuides: selectedGuideIdsRef.current.size > 0,
        canSelectShapes,
        isInShapeCreationMode,
        isNodeEditMode,
      })

      if (action) runCanvasKeyAction(action, e, isEditable, context)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    applyPanScroll,
    bleedHeight,
    bleedWidth,
    isInShapeCreationMode,
    isInPenToolMode,
    isNodeEditMode,
    isInTextCreationMode,
    activeItem,
    isPanInteractionActive,
    isPanModeActive,
    isTextToolActive,
    isEditableTarget,
    cancelTextLinking,
    isTextLinkingActive,
    canSelectShapes,
    onDeactivatePanMode,
    onSelectAllApplied,
    onSelectNoneApplied,
    onBackgroundClick,
    clearShapeSelectionWithHistory,
    clearUnifiedSelectionWithHistory,
    copySelectionToClipboard,
    cutSelectionToClipboard,
    cutSelectionForActiveMode,
    isTextEditSessionActive,
    selectAllForActiveTextEdit,
    handleDeleteKeyWhileTextEditing,
    refocusActiveTextEditor,
    deleteSelectedObjects,
    lockSelectedObjects,
    unlockSelectedObjects,
    toggleFreezeSelectedObjects,
    pasteFromClipboard,
    onDeleteShapesHandled,
    updateGuides,
    nudgeDistancePt,
    onShapesNudge,
    onLayerReorder,
    onUnifiedLayerArrange,
    onActivateTextTool,
    onActivateSelectTool,
    activeIsolationGroupId,
    exitGroupIsolationMode,
    onFreezeSelected,
    nodeEditShapeId,
    onNodeEditModeChange,
    canUnifiedBringForward,
    canUnifiedSendBackward,
    setGroupIsolationPath,
    setNodeEditShapeData,
    setNodeEditShapeId,
    exitNodeEditMode,
    internalShapeManagerRef,
    scrollRef,
    selectedGuideIdsRef,
    setSelectedGuideIds,
    zMarqueeKeyHeldRef,
    setZMarqueeKeyHeld,
    setIsMarqueeZoomMode,
    setMarqueeZoom,
  ])
}
